import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  calculateTakeHome,
  simulateTaxReduction,
  getTaxBandData,
  formatCurrency,
  TAX_BANDS,
} from '../utils/taxCalculations';
import { getInitialSimulationState } from '../utils/taxSources';
import TaxBreakdownChart from './TaxBreakdownChart';
import SimulationToggles from './SimulationToggles';
import TaxSummary from './TaxSummary';
import './TaxCalculator.css';

function TaxCalculator() {
  const [salary, setSalary] = useState('60,000');
  const [simulations, setSimulations] = useState(getInitialSimulationState);

  const numericSalary = parseFloat(salary.replace(/,/g, '')) || 0;

  // Calculate tax breakdown and simulations
  const calculation = calculateTakeHome(numericSalary);
  const simulationResult = simulateTaxReduction(numericSalary, simulations);
  const taxBandData = getTaxBandData(numericSalary);

  const handleSalaryChange = useCallback((e) => {
    const value = e.target.value;
    // Allow only numbers and commas
    const cleanValue = value.replace(/[^0-9,]/g, '');
    setSalary(cleanValue);
  }, []);

  const handleSalaryFocus = useCallback((e) => {
    // Clear the input when user focuses on it
    setSalary('');
  }, []);

  const handleSimulationToggle = useCallback((simulationType) => {
    setSimulations((prev) => ({
      ...prev,
      [simulationType]: !prev[simulationType],
    }));
  }, []);

  const hasSimulationsActive = Object.values(simulations).some(value => value);

  return (
    <div className="tax-calculator">
      {/* Hero/Input Section */}
      <section className="calculator-hero">
        <div className="hero-content">
          <h1>See How Much More You Could Take Home</h1>
          <p className="hero-subtitle">
            Discover how your take-home pay could increase if the government raised revenue from alternative sources
          </p>
          
          <div className="salary-input-container">
            <label htmlFor="salary-input" className="salary-label">
              Enter your annual salary
            </label>
            <div className="salary-input-wrapper">
              <span className="currency-symbol">£</span>
              <input
                id="salary-input"
                type="text"
                inputMode="numeric"
                value={salary}
                onChange={handleSalaryChange}
                onFocus={handleSalaryFocus}
                placeholder="50,000"
                className="salary-input"
                aria-describedby="salary-hint"
              />
            </div>
            <p id="salary-hint" className="salary-hint">
              Enter your gross annual salary before tax
            </p>
          </div>
        </div>
      </section>

      {/* Results Section */}
      {numericSalary > 0 && (
        <section className="results-section">
          <div className="results-container">
            {/* Tax Summary Cards */}
            <TaxSummary 
              calculation={calculation}
              simulationResult={simulationResult}
              hasSimulationsActive={hasSimulationsActive}
            />

            {/* Tax Breakdown Chart */}
            <div className="chart-section">
              <h2>Your Tax Breakdown</h2>
              <TaxBreakdownChart 
                taxBandData={taxBandData}
                calculation={calculation}
                simulationResult={hasSimulationsActive ? simulationResult : null}
              />
            </div>

            {/* Simulation Toggles */}
            <div className="simulation-section">
              <h2>What If Scenarios</h2>
              <p className="simulation-intro">
                Explore how alternative revenue sources could reduce your tax burden
              </p>
              <SimulationToggles 
                simulations={simulations}
                onToggle={handleSimulationToggle}
                savings={simulationResult.savings}
              />
            </div>

            {/* Detailed Breakdown */}
            <div className="breakdown-section">
              <h2>Detailed Breakdown</h2>
              <div className="breakdown-grid">
                <div className="breakdown-card">
                  <h3>Income Tax</h3>
                  <div className="breakdown-items">
                    <div className="breakdown-item">
                      <span>Personal Allowance (0%)</span>
                      <span>{formatCurrency(Math.min(numericSalary, TAX_BANDS.personalAllowance))}</span>
                    </div>
                    {calculation.incomeTax.basicRateTax > 0 && (
                      <div className="breakdown-item">
                        <span>Basic Rate (20%)</span>
                        <span className="tax-amount">-{formatCurrency(calculation.incomeTax.basicRateTax)}</span>
                      </div>
                    )}
                    {calculation.incomeTax.higherRateTax > 0 && (
                      <div className="breakdown-item">
                        <span>Higher Rate (40%)</span>
                        <span className="tax-amount">-{formatCurrency(calculation.incomeTax.higherRateTax)}</span>
                      </div>
                    )}
                    {calculation.incomeTax.additionalRateTax > 0 && (
                      <div className="breakdown-item">
                        <span>Additional Rate (45%)</span>
                        <span className="tax-amount">-{formatCurrency(calculation.incomeTax.additionalRateTax)}</span>
                      </div>
                    )}
                    <div className="breakdown-item total">
                      <span>Total Income Tax</span>
                      <span className="tax-amount">-{formatCurrency(calculation.incomeTax.totalTax)}</span>
                    </div>
                  </div>
                </div>

                <div className="breakdown-card">
                  <h3>National Insurance</h3>
                  <div className="breakdown-items">
                    {calculation.nationalInsurance.mainNI > 0 && (
                      <div className="breakdown-item">
                        <span>Main Rate (8%)</span>
                        <span className="tax-amount">-{formatCurrency(calculation.nationalInsurance.mainNI)}</span>
                      </div>
                    )}
                    {calculation.nationalInsurance.upperNI > 0 && (
                      <div className="breakdown-item">
                        <span>Upper Rate (2%)</span>
                        <span className="tax-amount">-{formatCurrency(calculation.nationalInsurance.upperNI)}</span>
                      </div>
                    )}
                    <div className="breakdown-item total">
                      <span>Total NI</span>
                      <span className="tax-amount">-{formatCurrency(calculation.nationalInsurance.totalNI)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {numericSalary === 0 && (
        <section className="empty-state">
          <div className="empty-content">
            <span className="empty-icon">📊</span>
            <h2>Enter your salary to get started</h2>
            <p>
              We'll show you a complete breakdown of your Income Tax and National Insurance,
              plus simulations showing how alternative policies could save you money.
            </p>
          </div>
        </section>
      )}

      {/* Info Section */}
      <section className="info-section">
        <div className="info-container">
          <div className="info-card">
            <h3>📚 Data Sources</h3>
            <p>Tax rates based on HMRC 2025-2026 guidelines. Simulation data from government statistics and independent research.</p>
          </div>
          <div className="info-card">
            <h3>🔒 Privacy</h3>
            <p>All calculations happen in your browser. We never store or transmit your salary data. <Link to="/privacy" className="info-link">Learn more</Link></p>
          </div>
          <div className="info-card">
            <h3>⚠️ Disclaimer</h3>
            <p>This tool provides estimates only. For official tax advice, consult HMRC or a qualified tax professional. <Link to="/terms" className="info-link">Terms apply</Link></p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TaxCalculator;
